<template>
  <div class="settings-payments">
    <Loader v-if="isLoading" :text="loadingMessage" />
    <div class="settings-block">
      <div class="settings-block__header">
        <div class="header-content">
          <span>Insurance Mapping</span>
          <v-btn
            color="primary"
            small
            @click="openAddCompanyModal"
            :disabled="isLoading"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add New Company
          </v-btn>
        </div>
      </div>

      <div class="search-section">
        <v-text-field
          v-model="searchTerm"
          label="Search by company name..."
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          hide-details
          class="search-field"
        />
        <div v-if="searchTerm.trim() && filteredInsurance.length > 0" class="search-results-info">
          Found {{ filteredInsurance.length }} of {{ tempInsurance.length }} companies
        </div>
      </div>

      <div v-if="searchTerm.trim() && filteredInsurance.length === 0" class="no-results">
        <v-icon class="mr-2" color="grey">mdi-magnify</v-icon>
        No companies found matching "{{ searchTerm }}"
      </div>
      <v-expansion-panels
        v-if="paginatedInsurance.length > 0"
        class="insurance-mapping-panels"
        multiple
      >
        <v-expansion-panel
          v-for="(map, index) of paginatedInsurance"
          :key="`insurance-${map.id || index}`"
          class="insurance-panel"
        >
          <v-expansion-panel-header class="insurance-panel__header">
            <div class="insurance-company-info">
              <div class="company-name">{{ map.company_name }}</div>
              <div class="company-aliases-preview">
                <span class="aliases-count">{{ getAliasCount(map.greenway_aliases) }} aliases</span>
                <span class="pverify-count">{{ getPverifyCount(map.pverify_codes) }} pverify codes</span>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="insurance-panel__content">
            <div class="aliases-section">
              <div class="aliases-header">
                <h4>Aliases</h4>
                <div v-if="insuranceItemView === getOriginalIndex(map)" class="edit-actions">
                  <v-btn
                    small
                    @click="onSubmitInsurance(map.id, getOriginalIndex(map))"
                    :disabled="!isReadyToUpdateInsurance"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-content-save</v-icon>
                    Save Changes
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    @click="editInsuranceSettings($event, getOriginalIndex(map))"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                </div>
              </div>

              <div class="aliases-container">
                <div class="aliases-list" v-if="map.greenway_aliases && map.greenway_aliases.length">
                  <div
                    v-for="(alias, ind) in map.greenway_aliases"
                    v-show="alias"
                    :key="`alias-${map.id}-${ind}`"
                    class="alias-row"
                  >
                    <div class="alias-content">
                      <v-text-field
                        v-if="isEditingAlias(map.id, ind)"
                        v-model="editingGreenwayAlias.alias"
                        hide-details
                        dense
                        outlined
                        class="alias-edit-field"
                        @keyup.enter="editAlias(map.id, getOriginalIndex(map), ind)"
                        @keyup.esc="toggleGreenwayEdit"
                      />
                      <span v-else class="alias-text">{{ alias }}</span>
                    </div>

                    <div class="alias-actions">
                      <template v-if="isEditingAlias(map.id, ind)">
                        <v-btn
                          icon
                          small
                          @click="editAlias(map.id, getOriginalIndex(map), ind)"
                          title="Save"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="toggleGreenwayEdit"
                          title="Cancel"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          icon
                          small
                          @click="toggleGreenwayEdit(map.id, ind, alias)"
                          title="Edit"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="removeAlias(map.id, getOriginalIndex(map), ind)"
                          title="Delete"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </div>
                  </div>
                </div>

                <div v-if="!map.greenway_aliases || !map.greenway_aliases.length" class="no-aliases">
                  <v-icon class="mr-2">mdi-information-outline</v-icon>
                  No aliases configured for this insurance company
                </div>

                <div class="add-alias-section">
                  <div class="add-alias-header">
                    <span>Add New Alias:</span>
                    <v-btn
                      v-if="insuranceItemView !== getOriginalIndex(map)"
                      text
                      small
                      @click="editInsuranceSettings($event, getOriginalIndex(map))"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add Alias
                    </v-btn>
                  </div>

                  <div v-if="insuranceItemView === getOriginalIndex(map)" class="add-alias-form">
                    <v-text-field
                      v-model="tempGreenwayAlias"
                      label="Enter new alias"
                      outlined
                      dense
                      hide-details
                      class="add-alias-field"
                      @keyup.enter="addAlias(getOriginalIndex(map))"
                    />
                    <v-btn
                      icon
                      @click="addAlias(getOriginalIndex(map))"
                      :disabled="!tempGreenwayAlias"
                      title="Add Alias"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>

            <div class="pverify-codes-separator">
              <hr style="border: 0; border-top: 2px solid #007bff; margin: 2rem 0 1rem 0; background: #007bff;">
            </div>
            <div class="pverify-section">
              <div class="pverify-header">
                <h4>PVerify Codes</h4>
                <div v-if="pverifyItemView === getOriginalIndex(map)" class="edit-actions">
                  <v-btn
                    small
                    @click="onSubmitPverify(map.id, getOriginalIndex(map))"
                    :disabled="!isReadyToUpdateInsurance"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-content-save</v-icon>
                    Save Changes
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    @click="editPverifySettings($event, getOriginalIndex(map))"
                  >
                    <v-icon left small>mdi-close</v-icon>
                    Cancel
                  </v-btn>
                </div>
              </div>

              <div class="pverify-container">
                <div class="pverify-list" v-if="map.pverify_codes && map.pverify_codes.length">
                  <div
                    v-for="(code, ind) in map.pverify_codes"
                    v-show="code"
                    :key="`pverify-${map.id}-${ind}`"
                    class="pverify-row"
                    style="display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; border-bottom: 1px solid #f0f0f0;"
                  >
                    <div class="pverify-content" style="flex: 1; margin-right: 1rem;">
                      <v-text-field
                        v-if="isEditingPverify(map.id, ind)"
                        v-model="editingPverifyCode.code"
                        hide-details
                        dense
                        outlined
                        class="pverify-edit-field"
                        @keyup.enter="editPverifyCode(map.id, getOriginalIndex(map), ind)"
                        @keyup.esc="togglePverifyEdit"
                      />
                      <span v-else class="pverify-text">{{ code }}</span>
                    </div>

                    <div class="pverify-actions" style="display: flex; gap: 0.25rem;">
                      <template v-if="isEditingPverify(map.id, ind)">
                        <v-btn
                          icon
                          small
                          @click="editPverifyCode(map.id, getOriginalIndex(map), ind)"
                          title="Save"
                        >
                          <v-icon small>mdi-check</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="togglePverifyEdit"
                          title="Cancel"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          icon
                          small
                          @click="togglePverifyEdit(map.id, ind, code)"
                          title="Edit"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          small
                          @click="removePverifyCode(map.id, getOriginalIndex(map), ind)"
                          title="Delete"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                    </div>
                  </div>
                </div>

                <div v-if="!map.pverify_codes || !map.pverify_codes.length" class="no-pverify">
                  <v-icon class="mr-2">mdi-information-outline</v-icon>
                  No PVerify codes configured for this insurance company
                </div>

                <div class="add-pverify-section" style="margin-top: 1rem; padding-top: 1rem;">
                  <div class="add-pverify-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                    <span style="font-weight: 500; color: #333;">Add New PVerify Code:</span>
                    <v-btn
                      v-if="pverifyItemView !== getOriginalIndex(map)"
                      text
                      small
                      @click="editPverifySettings($event, getOriginalIndex(map))"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add PVerify Code
                    </v-btn>
                  </div>

                  <div v-if="pverifyItemView === getOriginalIndex(map)" class="add-pverify-form" style="display: flex; gap: 0.5rem; align-items: flex-start;">
                    <v-text-field
                      v-model="tempPverifyCode"
                      label="Enter new PVerify code"
                      outlined
                      dense
                      hide-details
                      class="add-pverify-field"
                      style="flex: 1; max-width: 300px;"
                      @keyup.enter="addPverifyCode(getOriginalIndex(map))"
                    />
                    <v-btn
                      icon
                      @click="addPverifyCode(getOriginalIndex(map))"
                      :disabled="!tempPverifyCode"
                      title="Add PVerify Code"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-if="!searchTerm.trim() && totalPages > 1" class="pagination-section">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          color="primary"
          class="mt-3"
          @input="onPageChange"
        />
      </div>
    </div>

    <div class="settings-block">
      <div class="settings-block__header">Insurance Allowables</div>
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <th>COMPANY ID</th>
            <th>INSURANCE</th>
            <th>CPT</th>
            <th>ALLOWABLE</th>
            <th></th>
          </thead>
          <tbody>
            <template v-for="(item, index) of tempAllowableList">
              <tr
                :key="index"
                class="settings-block__item"
                :class="{
                  'settings-block__item-selected': allowableItemView === index,
                }"
              >
                <td>{{ item.id }}</td>
                <td>{{ item.insurance }}</td>
                <td>{{ item.cpt_code }}</td>
                <td>${{ item.amount }}</td>
                <td>
                  <button
                    class="btn settings-block__edit"
                    type="button"
                    v-if="allowableItemView !== index"
                    v-on:click.prevent="editAllowable($event, index)"
                  >
                    Edit
                  </button>
                  <button
                    class="
                      btn
                      settings-block__edit settings-block__edit-success
                    "
                    v-else
                    @click.prevent="onSubmitAllowable(item.id, index)"
                  >
                    Save
                  </button>
                  <br />
                  <button
                    class="btn settings-block__edit"
                    type="button"
                    v-if="allowableItemView === index"
                    v-on:click.prevent="editAllowable($event, index)"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
              <tr
                v-if="allowableItemView === index"
                :key="`_${index}`"
                class="settings-block__item"
                :class="{
                  'settings-block__item-selected': allowableItemView === index,
                }"
              >
                <td colspan="5">
                  <div class="settings-block__item-details">
                    <div class="settings-item settings-item_c6of12">
                      <v-text-field
                        label="CPT:"
                        name="number"
                        v-on:keyup="updateAllowableData($event, index)"
                        v-model="item.cpt_code"
                      >
                      </v-text-field>
                    </div>
                    <div class="settings-item settings-item_c6of12">
                      <v-text-field
                        label="ALLOWABLE($):"
                        name="amount"
                        v-on:keyup="updateAllowableData($event, index)"
                        v-model="item.amount"
                      >
                      </v-text-field>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
    </div>

    <div class="settings-block">
      <div class="settings-block__header">Defaults</div>
      <button
        class="btn settings-block__edit-btn settings-block__edit-btn-success"
        v-if="defaultsSettingsEditable"
        v-on:click.prevent="saveDefaultsSettings"
      >
        Save
      </button>
      <button
        class="btn settings-block__edit-btn settings-block__edit-btn-cancel"
        v-if="defaultsSettingsEditable"
        v-on:click.prevent="editDefaultsSettings"
      >
        Cancel
      </button>
      <button
        class="btn settings-block__edit-btn"
        v-if="!defaultsSettingsEditable"
        v-on:click.prevent="editDefaultsSettings"
      >
        Edit
      </button>
      <div class="settings-block__defaults">
        <ValidationObserver>
          <form>
            <table>
              <tbody>
                <tr>
                  <td>
                    <v-row>
                      <v-col cols="12" md="6">
                        <ValidationProvider
                          name="paymentDenominator"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            label="Prepay Plan Max Terms"
                            name="paymentDenominator"
                            v-bind:disabled="!defaultsSettingsEditable"
                            v-on:keyup="updateDefaultsData"
                            v-model="tempDefaults.paymentDenominator"
                          >
                          </v-text-field>
                          <div class="invalid-data">{{ errors[0] }}</div>
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="12" md="6">
                        <ValidationProvider
                          name="LicenseVersion"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            label="License Version"
                            name="LicenseVersion"
                            v-bind:disabled="!defaultsSettingsEditable"
                            v-on:keyup="updateDefaultsData"
                            v-model="tempDefaults.LicenseVersion"
                          >
                          </v-text-field>
                          <div class="invalid-data">{{ errors[0] }}</div>
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <div class="form-group">
                      <ValidationProvider
                        name="estimationText"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-textarea
                          label="Cost Estimate Text"
                          name="estimationText"
                          v-bind:disabled="!defaultsSettingsEditable"
                          v-model="tempDefaults.estimationText"
                          v-on:keyup="updateDefaultsData"
                        ></v-textarea>
                        <div class="invalid-data">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <div class="form-group">
                      <ValidationProvider
                        name="policyText"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-textarea
                          label="Policy Text"
                          name="policyText"
                          v-bind:disabled="!defaultsSettingsEditable"
                          v-model="tempDefaults.policyText"
                          v-on:keyup="updateDefaultsData"
                        ></v-textarea>
                        <div class="invalid-data">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td colspan="2">
                    <div class="form-group">
                      <ValidationProvider
                        name="CustomerName"
                        rules="required"
                        v-slot="{ errors }"
                      >
                        <v-textarea
                          label="License Text"
                          name="LicenseText"
                          v-bind:disabled="!defaultsSettingsEditable"
                          v-model="tempDefaults.LicenseText"
                          v-on:keyup="updateDefaultsData"
                        ></v-textarea>
                        <div class="invalid-data">{{ errors[0] }}</div>
                      </ValidationProvider>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </form>
        </ValidationObserver>
      </div>
    </div>

    <!-- Add Company Modal -->
    <AddCompanyModal
      v-model="showAddCompanyModal"
      :loading="addCompanyLoading"
      @submit="onAddCompanySubmit"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import Loader from "@/components/loader/loader.vue";
import AddCompanyModal from "@/components/modals/add-company-modal/add-company-modal.vue";

const { mapActions: customerActions, mapGetters: customerGetters } =
  createNamespacedHelpers("customer");

export default {
  name: "SettingsPayments",
  props: {
    allowables: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      tempDefaults: {},
      updatedDataDefaults: {},
      defaultsSettingsEditable: false,
      insuranceItemView: null,
      pverifyItemView: null,
      allowableItemView: null,
      tempInsurance: [],
      tempAllowableList: [],
      tempGreenwayAlias: "",
      tempPverifyCode: "",
      isReadyToUpdateInsurance: false,
      tempAllowableData: null,
      searchTerm: "",
      editingGreenwayAlias: {
        companyId: null,
        greenwayIndex: null,
        alias: "",
      },
      editingPverifyCode: {
        companyId: null,
        pverifyIndex: null,
        code: "",
      },
      isLoading: false,
      loadingMessage: "",
      currentPage: 1,
      pageSize: 20,
      debouncedSearchTerm: "",
      searchDebounceTimer: null,
      showAddCompanyModal: false,
      addCompanyLoading: false,
    };
  },
  components: {
    ValidationProvider,
    ValidationObserver,
    Loader,
    AddCompanyModal,
  },
  watch: {
    allowables(value) {
      this.tempAllowableList = [...value];
    },
    customerMappings(value) {
      this.tempInsurance = value.map(company => ({
        ...company,
        greenway_aliases: company.greenway_aliases ? [...company.greenway_aliases] : [],
        pverify_codes: company.pverify_codes ? [...company.pverify_codes] : []
      }));
    },
    searchTerm(newTerm) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      if (!newTerm.trim()) {
        this.debouncedSearchTerm = "";
        this.currentPage = 1;
        return;
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchTerm = newTerm;
        this.currentPage = 1;
      }, 300);
    },
  },
  computed: {
    ...customerGetters({
      customer: "CUSTOMER",
      customerMappings: "CUSTOMER_MAPPINGS",
    }),
    filteredInsurance() {
      if (!this.debouncedSearchTerm.trim()) {
        return this.tempInsurance;
      }
      const searchLower = this.debouncedSearchTerm.toLowerCase().trim();
      return this.tempInsurance.filter(company =>
        company.company_name &&
        company.company_name.toLowerCase().includes(searchLower)
      );
    },
    paginatedInsurance() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredInsurance.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredInsurance.length / this.pageSize);
    },
  },
  mounted() {
    this.tempDefaults = Object.assign({}, this.customer);
    this.tempInsurance = this.customerMappings.map(company => ({
      ...company,
      greenway_aliases: company.greenway_aliases ? [...company.greenway_aliases] : [],
      pverify_codes: company.pverify_codes ? [...company.pverify_codes] : []
    }));
    this.tempAllowableList = [...this.allowables];
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    ...customerActions(["deleteGreenwayAlias", "updateCustomerMappings", "fetchCustomerMappings"]),
    getAliasCount(aliases) {
      if (!aliases || !aliases.length) return 0;
      let count = 0;
      for (let i = 0; i < aliases.length; i++) {
        if (aliases[i]) count++;
      }
      return count;
    },
    getPverifyCount(pverifyCodes) {
      if (!pverifyCodes || !pverifyCodes.length) return 0;
      let count = 0;
      for (let i = 0; i < pverifyCodes.length; i++) {
        if (pverifyCodes[i]) count++;
      }
      return count;
    },
    getOriginalIndex(company) {
      return this.tempInsurance.findIndex(c => c.id === company.id);
    },
    isEditingAlias(companyId, greenwayIndex) {
      return this.editingGreenwayAlias.companyId === companyId &&
             this.editingGreenwayAlias.greenwayIndex === greenwayIndex;
    },
    isEditingPverify(companyId, pverifyIndex) {
      return this.editingPverifyCode.companyId === companyId &&
             this.editingPverifyCode.pverifyIndex === pverifyIndex;
    },
    editInsuranceSettings(e, index) {
      if (e && e.target && e.target.desabled) {
        return;
      }
      this.tempGreenwayAlias = "";
      this.tempPverifyCode = "";
      this.tempInsurance = this.customerMappings.map(company => ({
        ...company,
        greenway_aliases: company.greenway_aliases ? [...company.greenway_aliases] : [],
        pverify_codes: company.pverify_codes ? [...company.pverify_codes] : []
      }));
      this.insuranceItemView = index !== this.insuranceItemView ? index : null;
    },
    editPverifySettings(e, index) {
      if (e && e.target && e.target.desabled) {
        return;
      }
      this.tempPverifyCode = "";
      this.tempInsurance = this.customerMappings.map(company => ({
        ...company,
        greenway_aliases: company.greenway_aliases ? [...company.greenway_aliases] : [],
        pverify_codes: company.pverify_codes ? [...company.pverify_codes] : []
      }));
      this.pverifyItemView = index !== this.pverifyItemView ? index : null;
    },
    editDefaultsSettings(e) {
      if (!e || e.target.desabled) {
        return;
      }
      this.updatedDataDefaults = {};
      this.isReadyToUpdateInsurance = false;
      this.tempDefaults = Object.assign({}, this.customer);
      this.defaultsSettingsEditable = !this.defaultsSettingsEditable;
    },
    editAllowable(e, index) {
      if (!e || e.target.desabled) {
        return;
      }
      this.tempAllowableList = [...this.allowables];
      this.tempAllowableData = this.tempAllowableList[index];
      this.allowableItemView = index !== this.allowableItemView ? index : null;
    },
    saveDefaultsSettings() {
      this.defaultsSettingsEditable = !this.defaultsSettingsEditable;
      this.onSubmitDefaults();
    },
    updateInsuranceData(e) {
      this.tempGreenwayAlias = e.target.value;
    },
    updateDefaultsData(e) {
      let el = e.target;
      if (el.value != this.customer[el.name]) {
        this.updatedDataDefaults = {
          ...this.updatedDataDefaults,
          [el.name]: el.value,
        };
      } else {
        if (this.updatedDataDefaults[el.name]) {
          delete this.updatedDataDefaults[el.name];
        }
      }
    },
    updateAllowableData(e) {
      let el = e.target;
      this.tempAllowableData[el.name] = el.value;
    },
    addAlias(index) {
      if (this.tempGreenwayAlias) {
        this.tempInsurance[index].greenway_aliases.push(this.tempGreenwayAlias);
        this.tempGreenwayAlias = "";
        this.isReadyToUpdateInsurance = true;
      }
    },
    addPverifyCode(index) {
      if (this.tempPverifyCode) {
        if (!this.tempInsurance[index].pverify_codes) {
          this.tempInsurance[index].pverify_codes = [];
        }
        this.tempInsurance[index].pverify_codes.push(this.tempPverifyCode);
        this.tempPverifyCode = "";
        this.isReadyToUpdateInsurance = true;
      }
    },
    togglePverifyEdit(companyId, pverifyIndex, code) {
      if (this.editingPverifyCode.code && code) return;

      this.editingPverifyCode.companyId = companyId;
      this.editingPverifyCode.pverifyIndex = pverifyIndex;
      this.editingPverifyCode.code = code || "";
    },
    async editPverifyCode(insuranceMappingId, mapIndex, pverifyIndex) {
      this.isLoading = true;
      this.loadingMessage = "Updating pverify code";

      if (this.tempInsurance[mapIndex] && this.tempInsurance[mapIndex].pverify_codes) {
        this.tempInsurance[mapIndex].pverify_codes[pverifyIndex] = this.editingPverifyCode.code;
        this.isReadyToUpdateInsurance = true;
      }

      this.togglePverifyEdit();
      this.isLoading = false;
      this.loadingMessage = "";
    },
    async removePverifyCode(insuranceMappingId, mapIndex, pverifyIndex) {
      this.isLoading = true;
      this.loadingMessage = "Deleting pverify code";

      if (this.tempInsurance[mapIndex] && this.tempInsurance[mapIndex].pverify_codes) {
        this.tempInsurance[mapIndex].pverify_codes.splice(pverifyIndex, 1);
        this.isReadyToUpdateInsurance = true;
      }

      this.isLoading = false;
      this.loadingMessage = "";
    },
    stringifyAliases(data) {
      const stringifiedData = {};
      data.forEach((element, index) => {
        stringifiedData[index] = element;
      });
      return JSON.stringify(stringifiedData);
    },
    onSubmitDefaults() {
      if (Object.keys(this.updatedDataDefaults).length) {
        this.isLoading = true;
        this.loadingMessage = "Updating data...";

        window.axios
          .post("/proxy/change-customer-info", {
            updated_type: "customer",
            updated_data: this.updatedDataDefaults,
            destination_id: this.customer.customer_id,
          })
          .then((response) => {
            if (response.data && response.data.success === true) {
              this.$store.commit("customer/UPD_CUSTOMER", this.tempDefaults);
              this.editDefaultsSettings();
            }
            this.isLoading = false;
            this.loadingMessage = "";
          });
      } else {
        this.editDefaultsSettings();
      }
    },
    onSubmitInsurance(id, index) {
      if (!this.isReadyToUpdateInsurance) return;
      this.isLoading = true;
      this.loadingMessage = "Updating data...";
      const URL = "/proxy/set-customer-mappings";
      return window.axios
        .post(URL, {
          id: id,
          customer_id: this.customer.customer_id,
          updatedFields: {
            greenway_aliases: this.tempInsurance[index].greenway_aliases,
            pverify_codes: this.tempInsurance[index].pverify_codes,
          },
        })
        .then((response) => {
          if (response && response.data.success) {
            this.$store.commit("customer/SET_CUSTOMER_MAPPINGS", this.tempInsurance);
            this.insuranceItemView = null;
            this.pverifyItemView = null;
          } else throw new Error(`Submit Insurance response invalid`);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.isLoading = false;
          this.loadingMessage = "";
        });
    },
    onSubmitPverify(id, index) {
      if (!this.isReadyToUpdateInsurance) return;
      this.isLoading = true;
      this.loadingMessage = "Updating pverify codes...";
      const URL = "/proxy/set-customer-mappings";
      return window.axios
        .post(URL, {
          id: id,
          customer_id: this.customer.customer_id,
          updatedFields: {
            pverify_codes: this.tempInsurance[index].pverify_codes,
          },
        })
        .then((response) => {
          if (response && response.data.success) {
            this.$store.commit("customer/SET_CUSTOMER_MAPPINGS", this.tempInsurance);
            this.pverifyItemView = null;
          } else throw new Error(`Submit PVerify response invalid`);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.isLoading = false;
          this.loadingMessage = "";
        });
    },
    onSubmitAllowable(id, index) {
      this.isLoading = true;
      this.loadingMessage = "Updating data...";
      const URL = "/proxy/set-customer-mappings";
      return window.axios
        .post(URL, {
          id: id,
          customer_id: this.customer.customer_id,
          updatedFields: {
            allowables: {
              [this.tempAllowableData.cpt_code]: {
                cpt_code: this.tempAllowableData.cpt_code,
                amount: this.tempAllowableData.amount,
              },
            },
          },
        })
        .then((response) => {
          if (response && response.data.success) {
            this.tempAllowableList[index].amount =
              this.tempAllowableData.amount;
            this.tempAllowableList[index].cpt_code =
              this.tempAllowableData.cpt_code;
            this.$emit("onUpdateInsurance", this.tempAllowableList);
            this.allowableItemView = null;
          } else throw new Error(`Submit Insurance Allowable response invalid`);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.isLoading = false;
          this.loadingMessage = "";
        });
    },
    toggleGreenwayEdit(companyId, greenwayIndex, alias) {
      if (this.editingGreenwayAlias.alias && alias) return;

      this.editingGreenwayAlias.companyId = companyId;
      this.editingGreenwayAlias.greenwayIndex = greenwayIndex;
      this.editingGreenwayAlias.alias = alias;
    },
    async editAlias(insuranceMappingId, mapIndex, aliasIndex) {
      this.isLoading = true;
      this.loadingMessage = "Updating alias";
      await this.updateCustomerMappings({
        insuranceMappingId,
        mapIndex,
        alias: this.editingGreenwayAlias.alias,
        aliasIndex,
      });
      this.toggleGreenwayEdit();

      this.$store.commit("shared/SET_CONTENT_PRELOADER", {
        show: false,
        text: "",
      });

      this.isLoading = false;
      this.loadingMessage = "";
    },
    async removeAlias(insuranceMappingId, mapIndex, aliasIndex) {
      this.isLoading = true;
      this.loadingMessage = "Deleting alias";
      await this.deleteGreenwayAlias({
        insuranceMappingId,
        mapIndex,
        aliasIndex,
      });

      this.isLoading = false;
      this.loadingMessage = "";
    },
    onPageChange(page) {
      this.currentPage = page;
      this.insuranceItemView = null;
      this.pverifyItemView = null;
    },

    // Add Company Modal methods
    openAddCompanyModal() {
      this.showAddCompanyModal = true;
    },

    async onAddCompanySubmit(companyData) {
      this.addCompanyLoading = true;

      try {
        const response = await window.axios.post("/proxy/add-insurance-company", {
          ...companyData,
        });

        if (response && response.data && response.data.success) {
          // Show success message first
          this.$store.commit("notifications/ADD_NOTIFICATION", {
            type: "success",
            message: "Insurance company added successfully",
          });

          // Close the modal
          this.showAddCompanyModal = false;

          // Reset pagination to show the new company
          this.currentPage = 1;
          this.searchTerm = "";

          // Refresh the insurance mappings
          await this.fetchCustomerMappings();
        } else {
          throw new Error('Failed to add insurance company');
        }
      } catch (error) {
        console.error('Error adding company:', error);

        // Show error message
        this.$store.commit("notifications/ADD_NOTIFICATION", {
          type: "error",
          message: "Failed to add insurance company. Please try again.",
        });
      } finally {
        this.addCompanyLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/sass/variables.scss";

.settings-payments {
  .settings-block {
    margin-bottom: 2rem;

    &__header {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #ddd;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 1rem;
      }
    }
  }

  .search-section {
    margin-bottom: 1rem;

    .search-field {
      max-width: 400px;
    }

    .search-results-info {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      color: #6c757d;
      font-style: italic;
    }
  }

  .pagination-info {
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: #495057;
    font-weight: 500;
    text-align: center;
    background: #f8f9fa;
    padding: 0.5rem;
    border-radius: 4px;
    border: 1px solid #e9ecef;
  }

  .pagination-section {
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;

    .v-pagination {
      ::v-deep .v-pagination__item {
        box-shadow: none;
        border: 1px solid #ddd;

        &--active {
          background-color: #007bff !important;
          color: white !important;
          border-color: #007bff !important;
        }
      }

      ::v-deep .v-pagination__navigation {
        box-shadow: none;
        border: 1px solid #ddd;
      }
    }
  }

  .no-results {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: #6c757d;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 4px;
    border: 1px solid #e9ecef;
  }

  .insurance-mapping-panels {
    border: 1px solid #ddd;
    border-radius: 4px;

    .insurance-panel {
      border-bottom: 1px solid #ddd;

      &:last-child {
        border-bottom: none;
      }

      &__header {
        background: #f8f9fa !important;
        padding: 1rem;
        border: none;
        transition: none !important;

        &:hover {
          background: #f1f3f4 !important;
        }

        .insurance-company-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;

          .company-name {
            font-size: 1rem;
            font-weight: 500;
            color: #333;
          }

          .company-aliases-preview {
            .aliases-count {
              background: #e9ecef;
              color: #495057;
              padding: 0.2rem 0.5rem;
              border-radius: 3px;
              font-size: 0.8rem;
            }
          }
        }
      }

      &__content {
        padding: 1rem;
        background: #fff;
        border-top: 1px solid #eee;

        .aliases-section {
          .aliases-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;

            h4 {
              margin: 0;
              color: #333;
              font-weight: 500;
              font-size: 1rem;
              display: flex;
              align-items: center;
            }

            .edit-actions {
              display: flex;
              gap: 0.5rem;
            }
          }

          .aliases-container {
            .aliases-list {
              margin-bottom: 1rem;

              .alias-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.5rem 0;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                  border-bottom: none;
                }

                .alias-content {
                  flex: 1;
                  margin-right: 1rem;

                  .alias-text {
                    color: #333;
                    font-size: 0.9rem;
                  }

                  .alias-edit-field {
                    max-width: 200px;

                    ::v-deep .v-input__control {
                      min-height: 30px;
                    }

                    ::v-deep input {
                      padding: 6px 12px;
                      font-size: 0.9rem;
                    }
                  }
                }

                .alias-actions {
                  display: flex;
                  gap: 0.25rem;
                }
              }
            }

            .no-aliases {
              display: flex;
              align-items: center;
              padding: 1rem;
              background: #f8f9fa;
              border-radius: 4px;
              color: #6c757d;
              font-style: italic;
              justify-content: center;
            }

            .add-alias-section {
              margin-top: 1rem;
              padding-top: 1rem;
              border-top: 1px solid #eee;

              .add-alias-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.5rem;

                span {
                  font-weight: 500;
                  color: #333;
                }
              }

              .add-alias-form {
                display: flex;
                gap: 0.5rem;
                align-items: flex-start;

                .add-alias-field {
                  flex: 1;
                  max-width: 300px;
                }
              }
            }
          }

          .pverify-codes-separator {
            margin: 2rem 0 1rem 0;
            border-top: 3px solid #007bff;
            position: relative;

            &::after {
              content: '';
              position: absolute;
              top: -1px;
              left: 0;
              right: 0;
              height: 1px;
              background: rgba(0, 123, 255, 0.3);
            }
          }

          .pverify-section {
            margin-top: 0;

            .pverify-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1rem;

              h4 {
                margin: 0;
                color: #333;
                font-weight: 500;
                font-size: 1rem;
                display: flex;
                align-items: center;
              }

              .edit-actions {
                display: flex;
                gap: 0.5rem;
              }
            }

            .pverify-container {
              .pverify-list {
                margin-bottom: 1rem;

                .pverify-row {
                  display: flex !important;
                  justify-content: space-between !important;
                  align-items: center !important;
                  padding: 0.5rem 0 !important;
                  border-bottom: 1px solid #f0f0f0 !important;

                  &:last-child {
                    border-bottom: none !important;
                  }

                  .pverify-content {
                    flex: 1 !important;
                    margin-right: 1rem !important;

                    .pverify-text {
                      color: #333 !important;
                      font-size: 0.9rem !important;
                    }

                    .pverify-edit-field {
                      max-width: 200px !important;

                      ::v-deep .v-input__control {
                        min-height: 30px !important;
                      }

                      ::v-deep input {
                        padding: 6px 12px !important;
                        font-size: 0.9rem !important;
                      }
                    }
                  }

                  .pverify-actions {
                    display: flex !important;
                    gap: 0.25rem !important;
                  }
                }
              }

              .no-pverify {
                display: flex;
                align-items: center;
                padding: 1rem;
                background: #f8f9fa;
                border-radius: 4px;
                color: #6c757d;
                font-style: italic;
                justify-content: center;
              }

              .add-pverify-section {
                margin-top: 1rem;
                padding-top: 1rem;

                .add-pverify-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 0.5rem;

                  span {
                    font-weight: 500;
                    color: #333;
                  }
                }

                .add-pverify-form {
                  display: flex;
                  gap: 0.5rem;
                  align-items: flex-start;

                  .add-pverify-field {
                    flex: 1;
                    max-width: 300px;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .company-aliases-preview {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .aliases-count, .pverify-count {
      background: #e9ecef;
      color: #495057;
      padding: 0.2rem 0.5rem;
      border-radius: 3px;
      font-size: 0.8rem;
    }

    .pverify-count {
      background: #d1ecf1;
      color: #0c5460;
    }
  }

  .pverify-row {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 100% !important;
  }

  .pverify-content {
    flex: 1 !important;
    margin-right: 1rem !important;
  }

  .pverify-actions {
    display: flex !important;
    gap: 0.25rem !important;
    flex-shrink: 0 !important;
  }

  .v-expansion-panel,
  .v-expansion-panel-header,
  .v-expansion-panel-content,
  .v-expansion-panel-content__wrap {
    transition: none !important;
    animation: none !important;
  }

  .insurance-mapping-panels {
    .v-expansion-panel-content__wrap {
      transition: none !important;
      animation-duration: 0s !important;
      will-change: auto !important;
    }

    .alias-row {
      contain: layout style;
    }

    .insurance-panel {
      contain: layout style;

      &__header {
        contain: layout style paint;
        will-change: auto;
      }

      &__content {
        contain: layout style;
      }
    }

    .insurance-mapping-panels {
      transform: translateZ(0);
      -webkit-transform: translateZ(0);
    }
  }

  .v-btn {
    text-transform: none;
    font-weight: normal;
  }

  .v-text-field--outlined {
    ::v-deep .v-input__control {
      border-radius: 4px;
    }
  }

  @media (max-width: 768px) {
    .insurance-mapping-panels {
      .insurance-panel {
        &__header {
          padding: 0.75rem;

          .insurance-company-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
        }

        &__content {
          padding: 0.75rem;

          .aliases-section {
            .aliases-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;

              .edit-actions {
                align-self: stretch;
                justify-content: space-between;
              }
            }

            .aliases-container {
              .add-alias-section {
                .add-alias-form {
                  flex-direction: column;

                  .add-alias-field {
                    max-width: none;
                  }
                }
              }

            }
          }

          .pverify-section {
            .pverify-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 0.5rem;

              .edit-actions {
                align-self: stretch;
                justify-content: space-between;
              }
            }

            .pverify-container {
              .add-pverify-section {
                .add-pverify-form {
                  flex-direction: column;

                  .add-pverify-field {
                    max-width: none;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .greenway-aliases {
    display: flex;
    align-items: center;

    .greenway-buttons {
      display: flex;
    }
  }
}
</style>
